/**
 * @packageDocumentation Icon
 *
 * Component that render icon with a set of prop
 *
 * @category Component
 */

import clsx from 'clsx';
import { ComponentProps, CSSProperties, SVGProps } from 'react';
import styles from './Icon.module.scss';

/**
 * The props type for {@link Icon}.
 *
 * @category Props
 *
 */
export interface IconProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  component: React.FC<
    SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  style?: CSSProperties;
}

export type IconComponentProps = Pick<ComponentProps<IconProps['component']>, 'color' | 'onClick' | 'cursor' | 'role'>;

/**
 * Component that render icon with a set of props.
 *
 * ```tsx
 * import Icon from '~/components/data-display/Icon';
 *
 * ...
 *
 *   <Icon component={IconIdentityType} size="md" />
 *
 * ```
 *
 * @param {IconProps} props
 * @param props.size  - (optional) - one of 'xs' | 'sm' | 'md' | 'lg' | 'xl' default to 'md'
 * @param props.component - svg component React.FC<SVGProps<SVGSVGElement>>
 * @param props.style - (optional) - CSSProperties object
 * @param props.className - (optional) - extra class name
 *
 * @category Component
 */
const Icon: React.FC<IconProps & IconComponentProps> = ({
  component: Component,
  className,
  style,
  size = 'md',
  ...componentProps
}) => {
  return <Component className={clsx(styles.root, styles[size], className)} style={style} {...componentProps} />;
};

export default Icon;
